import React, { useState, useEffect } from "react";
import styles from "./statistics.module.css";
import PageTitle from "../../components/screens/page-title";
import { getEstadisticasConsolidadas, getUsersActivosAndPartes, getListaGestionExpedientes, getListaAdminFacturacion, getDashboardResumenComercial, downloadFacturacionComercial, getFacturacionPorComercial, getExpedientesPorComercial } from "../../services/apiService";
import usePageTitle from "../../hooks/usePageTitle";
import moment from "moment";
import { showSuccessToast, showErrorToast } from "../../utils/notifications";
import StatisticsDetailModal from "../../components/modals/StatisticsDetailModal";

const StatisticsPage = () => {
  usePageTitle('Estadísticas');
  
  const [stats, setStats] = useState({
    expedientes: { hoy: 0, semana: 0, mes: 0, trimestre: 0, anio: 0 },
    citas: { hoy: 0, semana: 0, mes: 0, trimestre: 0, anio: 0 },
    facturado: { hoy: 0, semana: 0, mes: 0, trimestre: 0, anio: 0 }
  });
  const [usersWithPartes, setUsersWithPartes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingUsers, setLoadingUsers] = useState(true);
  
  // Estados para el modal
  const [modalOpen, setModalOpen] = useState(false);
  const [modalData, setModalData] = useState(null);
  const [modalPeriod, setModalPeriod] = useState('');
  const [modalLoading, setModalLoading] = useState(false);

  useEffect(() => {
    const fetchStatistics = async () => {
      try {
        setLoading(true);
        const data = await getEstadisticasConsolidadas();
        setStats(data);
      } catch (error) {
        console.error("Error loading statistics:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchStatistics();
  }, []);

  useEffect(() => {
    const fetchUsersWithPartes = async () => {
      try {
        setLoadingUsers(true);
        const users = await getUsersActivosAndPartes();
        setUsersWithPartes(users);
      } catch (err) {
        console.error("Error fetching users with partes:", err);
      } finally {
        setLoadingUsers(false);
      }
    };

    fetchUsersWithPartes();
  }, []);

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('es-ES', {
      style: 'currency',
      currency: 'EUR'
    }).format(value);
  };

  const periodButtons = [
    { key: 'hoy', label: 'Hoy' },
    { key: 'semana', label: 'Esta semana' },
    { key: 'mes', label: 'Este mes' },
    { key: 'trimestre', label: 'Este trimestre' },
    { key: 'anio', label: 'Este año' }
  ];

  // Función para calcular fechas según el período
  const getDateRange = (period) => {
    const today = moment();
    let from, to;

    switch (period) {
      case 'hoy':
        from = today.format('YYYY-MM-DD');
        to = today.format('YYYY-MM-DD');
        break;
      case 'semana':
        from = today.startOf('week').format('YYYY-MM-DD');
        to = today.endOf('week').format('YYYY-MM-DD');
        break;
      case 'mes':
        from = today.startOf('month').format('YYYY-MM-DD');
        to = today.endOf('month').format('YYYY-MM-DD');
        break;
      case 'trimestre':
        from = today.startOf('quarter').format('YYYY-MM-DD');
        to = today.endOf('quarter').format('YYYY-MM-DD');
        break;
      case 'anio':
        from = today.startOf('year').format('YYYY-MM-DD');
        to = today.endOf('year').format('YYYY-MM-DD');
        break;
      default:
        from = '';
        to = '';
    }

    return { from, to };
  };

  // Handler para click en expedientes - AHORA ABRE MODAL
  const handleExpedientesClick = async (period) => {
    try {
      const { from, to } = getDateRange(period);
      console.log(`Abriendo modal de expedientes: ${period} (${from} - ${to})`);
      
      setModalPeriod(period);
      setModalOpen(true);
      setModalLoading(true);
      
      // Obtener datos del resumen por comerciales
      const data = await getDashboardResumenComercial(from, to);
      
      // Convertir los datos al formato que espera el modal
      const periodData = data.expedientes[period];
      const comercialesArray = Object.entries(periodData.porComercial)
        .filter(([nombre]) => nombre !== 'otros')
        .map(([nombre, total]) => ({ nombre, total }));
      
      setModalData({
        total: periodData.total,
        comerciales: comercialesArray,
        type: 'expedientes',
        from: from,
        to: to
      });
      
      setModalLoading(false);
    } catch (error) {
      console.error('Error al cargar datos de expedientes:', error);
      showErrorToast('Error al cargar los datos de expedientes');
      setModalOpen(false);
      setModalLoading(false);
    }
  };

  // Handler para click en facturado - AHORA ABRE MODAL
  const handleFacturadoClick = async (period) => {
    try {
      const { from, to } = getDateRange(period);
      console.log(`Abriendo modal de facturación: ${period} (${from} - ${to})`);
      
      setModalPeriod(period);
      setModalOpen(true);
      setModalLoading(true);
      
      // Obtener datos del resumen por comerciales
      const data = await getDashboardResumenComercial(from, to);
      
      // Convertir los datos al formato que espera el modal
      const periodData = data.facturado[period];
      const comercialesArray = Object.entries(periodData.porComercial)
        .filter(([nombre]) => nombre !== 'otros')
        .map(([nombre, total]) => ({ nombre, total }));
      
      setModalData({
        total: periodData.total,
        comerciales: comercialesArray,
        type: 'facturado',
        from: from,
        to: to
      });
      
      setModalLoading(false);
    } catch (error) {
      console.error('Error al cargar datos de facturación:', error);
      showErrorToast('Error al cargar los datos de facturación');
      setModalOpen(false);
      setModalLoading(false);
    }
  };

  // Handler para click en citas - ABRE MODAL SIN DESCARGA
  const handleCitasClick = async (period) => {
    try {
      const { from, to } = getDateRange(period);
      console.log(`Abriendo modal de citas: ${period} (${from} - ${to})`);
      
      setModalPeriod(period);
      setModalOpen(true);
      setModalLoading(true);
      
      // Obtener datos del resumen por comerciales
      const data = await getDashboardResumenComercial(from, to);
      
      // Convertir los datos al formato que espera el modal
      const periodData = data.citas[period];
      const comercialesArray = Object.entries(periodData.porComercial)
        .filter(([nombre]) => nombre !== 'otros')
        .map(([nombre, total]) => ({ nombre, total }));
      
      setModalData({
        total: periodData.total,
        comerciales: comercialesArray,
        type: 'citas',
        from: from,
        to: to
      });
      
      setModalLoading(false);
    } catch (error) {
      console.error('Error al cargar datos de citas:', error);
      showErrorToast('Error al cargar los datos de citas');
      setModalOpen(false);
      setModalLoading(false);
    }
  };

  // Handler para descargar desde el modal
  const handleDownloadFromModal = async () => {
    if (!modalData) return;
    
    try {
      const { from, to, type, comerciales } = modalData;
      console.log(`Descargando ${type}: (${from} - ${to})`);
      
      let blob;
      let filename;
      
      // Si hay comerciales, extraer sus IDs (asumiendo que necesitas buscarlos)
      // En este caso, como el backend filtra por nombre, necesitaríamos los IDs
      // Por simplicidad, vamos a descargar todo separado por comercial si hay datos de comerciales
      const tieneComerciales = comerciales && comerciales.length > 0;
      
      if (type === 'expedientes') {
        if (tieneComerciales) {
          // Descargar con separación por comercial
          blob = await getExpedientesPorComercial('', from, to, '');
          filename = `expedientes_por_comercial_${modalPeriod}_${moment().format('YYYY-MM-DD')}.xlsx`;
        } else {
          // Descarga normal
          blob = await getListaGestionExpedientes('', from, to, '');
          filename = `expedientes_${modalPeriod}_${moment().format('YYYY-MM-DD')}.xlsx`;
        }
      } else if (type === 'facturado') {
        if (tieneComerciales) {
          // Descargar con separación por comercial
          blob = await getFacturacionPorComercial('', from, to);
          filename = `facturacion_por_comercial_${modalPeriod}_${moment().format('YYYY-MM-DD')}.xlsx`;
        } else {
          // Descarga normal
          blob = await getListaAdminFacturacion('', from, to, '');
          filename = `facturacion_${modalPeriod}_${moment().format('YYYY-MM-DD')}.xlsx`;
        }
      }
      
      // Crear enlace de descarga
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      showSuccessToast('Archivo descargado correctamente');
    } catch (error) {
      console.error('Error al descargar:', error);
      showErrorToast('Error al descargar el archivo');
    }
  };

  // Handler para descargar un comercial específico
  const handleDownloadComercial = async (comercialNombre) => {
    if (!modalData) return;
    
    try {
      const { from, to, type } = modalData;
      console.log(`Descargando ${type} para ${comercialNombre}: (${from} - ${to})`);
      
      let blob;
      let filename;
      
      // El backend filtra por comercial basándose en el nombre proporcionado
      
      if (type === 'expedientes') {
        blob = await getExpedientesPorComercial('', from, to, '');
        filename = `expedientes_${comercialNombre.replace(/\s+/g, '_')}_${modalPeriod}_${moment().format('YYYY-MM-DD')}.xlsx`;
      } else if (type === 'facturado') {
        blob = await getFacturacionPorComercial('', from, to);
        filename = `facturacion_${comercialNombre.replace(/\s+/g, '_')}_${modalPeriod}_${moment().format('YYYY-MM-DD')}.xlsx`;
      }
      
      // Crear enlace de descarga
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      showSuccessToast(`Archivo de ${comercialNombre} descargado correctamente`);
    } catch (error) {
      console.error('Error al descargar:', error);
      showErrorToast('Error al descargar el archivo');
    }
  };

  return (
    <div className={styles.statisticsPage}>
      <PageTitle iconName="stats" title="Estadísticas" />
      
      <div className={styles.statsContainer}>
        {/* Expedientes Abiertos */}
        <div className={styles.statSection}>
          <h3 className={styles.sectionTitle}>EXPEDIENTES ABIERTOS</h3>
          <div className={styles.kpiRow}>
            {periodButtons.map(period => (
              <div 
                key={period.key} 
                className={styles.kpiCard}
                onClick={() => handleExpedientesClick(period.key)}
                style={{ cursor: 'pointer' }}
                title={`Click para ver detalle de expedientes de ${period.label.toLowerCase()}`}
              >
                <div className={styles.kpiLabel}>{period.label}</div>
                <div className={styles.kpiValue}>
                  {loading ? '...' : stats.expedientes[period.key]}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Citas */}
        <div className={styles.statSection}>
          <h3 className={styles.sectionTitle}>CITAS</h3>
          <div className={styles.kpiRow}>
            {periodButtons.map(period => (
              <div 
                key={period.key} 
                className={styles.kpiCard}
                onClick={() => handleCitasClick(period.key)}
                style={{ cursor: 'pointer' }}
                title={`Click para ver detalle de citas de ${period.label.toLowerCase()}`}
              >
                <div className={styles.kpiLabel}>{period.label}</div>
                <div className={styles.kpiValue}>
                  {loading ? '...' : stats.citas[period.key]}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Facturado */}
        <div className={styles.statSection}>
          <h3 className={styles.sectionTitle}>FACTURADO</h3>
          <div className={styles.kpiRow}>
            {periodButtons.map(period => (
              <div 
                key={period.key} 
                className={styles.kpiCard}
                onClick={() => handleFacturadoClick(period.key)}
                style={{ cursor: 'pointer' }}
                title={`Click para ver detalle de facturación de ${period.label.toLowerCase()}`}
              >
                <div className={styles.kpiLabel}>{period.label}</div>
                <div className={styles.kpiValue}>
                  {loading ? '...' : formatCurrency(stats.facturado[period.key])}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Pendientes de Partes */}
        <div className={styles.statSection}>
          <h3 className={styles.sectionTitle}>
            PENDIENTES DE PARTES
            {!loadingUsers && usersWithPartes.length > 0 && (
              <span style={{ marginLeft: '12px', color: 'var(--main)', fontWeight: '700' }}>
                (Total: {usersWithPartes.reduce((sum, user) => sum + user.partesAsignadas, 0)})
              </span>
            )}
          </h3>
          {loadingUsers ? (
            <div className={styles.loadingMessage}>Cargando...</div>
          ) : (
            <div className={styles.partesHorizontalRow}>
              {usersWithPartes.map((user) => (
                <div key={user.usuarioId} className={styles.parteCard}>
                  <span className={styles.parteUserName}>{user.nombre}</span>
                  <span className={styles.parteCount}>{user.partesAsignadas}</span>
                </div>
              ))}
              {usersWithPartes.length === 0 && !loadingUsers && (
                <div className={styles.emptyMessage}>
                  No hay partes asignados
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Modal de detalle de estadísticas */}
      <StatisticsDetailModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        data={modalData}
        period={modalPeriod}
        onDownload={handleDownloadFromModal}
        onDownloadComercial={handleDownloadComercial}
        loading={modalLoading}
      />
    </div>
  );
};

export default StatisticsPage;
